/**
 * Platform Health — Iteration 21C-C Phase 2 wired page.
 *
 * Reads liveness/readiness + integration health + audit-chain integrity
 * so operators can gauge production posture at a glance.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function PlatformHealth() {
  const [health, setHealth] = useState(null);
  const [selfCheck, setSelfCheck] = useState(null);
  const [chain, setChain] = useState(null);
  const [tamper, setTamper] = useState(null);
  const [ts, setTs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const results = await Promise.allSettled([
        api.get("/health"),
        api.get("/security/self-check"),
        api.get("/security/audit-chain/verify"),
        api.get("/security/audit-chain/tamper-check"),
      ]);
      setHealth(unwrap(results[0]));
      setSelfCheck(unwrap(results[1]));
      setChain(unwrap(results[2]));
      setTamper(unwrap(results[3]));
      setTs(new Date().toISOString());
    } catch (e) {
      toast.error("Health snapshot failed", { description: e.message });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-page-health">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Platform Health</h1>
          <p className="text-sm text-muted-foreground">
            Liveness · readiness · security posture · audit-chain integrity. Refreshes every 30s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {ts && <span className="text-xs text-muted-foreground">Refreshed {ts.slice(11, 19)} UTC</span>}
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="health-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Liveness" value={health?.status || "?"}
                  tone={health?.status === "ok" ? "ok" : "warn"}
                  testid="health-card-liveness" />
        <StatCard label="Runtime mode" value={selfCheck?.runtime_mode || "?"}
                  tone={selfCheck?.runtime_mode === "production" ? "ok" : "warn"}
                  testid="health-card-mode" />
        <StatCard label="Audit chain" value={chain?.status || "?"}
                  tone={chain?.status === "clean" ? "ok" : "warn"}
                  testid="health-card-chain" />
        <StatCard label="Tamper check" value={tamper?.status || "?"}
                  tone={tamper?.status === "clean" ? "ok" : "warn"}
                  testid="health-card-tamper" />
      </div>

      <PanelJson title="Security self-check" data={selfCheck} testid="health-self-check" />
      <PanelJson title="Audit chain verification" data={chain} testid="health-chain-verify" />
      <PanelJson title="Tamper detection" data={tamper} testid="health-tamper-check" />
    </div>
  );
}

function unwrap(res) {
  if (res.status === "fulfilled") return res.value?.data ?? null;
  return { error: res.reason?.response?.data?.detail || res.reason?.message || "unavailable" };
}

function StatCard({ label, value, tone, testid }) {
  const cls = tone === "ok" ? "text-green-600 dark:text-green-500"
                            : "text-yellow-600 dark:text-yellow-500";
  return (
    <div className="border rounded p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${cls}`}>{String(value)}</div>
    </div>
  );
}

function PanelJson({ title, data, testid }) {
  return (
    <div className="border rounded p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
      <pre className="text-[11px] font-mono whitespace-pre-wrap max-h-72 overflow-auto">
        {data ? JSON.stringify(data, null, 2) : "—"}
      </pre>
    </div>
  );
}
